import React, { useState } from "react";
import { StorageService } from "../../services/storage.js";
import {
  UserPlus,
  Check,
  X,
  AlertTriangle,
  KeyRound,
  Eye,
  EyeOff,
  Sparkles,
  Lock,
  CheckCircle2,
  ShieldCheck,
  Info
} from "lucide-react";
export const AdminUsers = ({ users, currentUser, onUsersUpdated }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [resettingUser, setResettingUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionSuccessMsg, setActionSuccessMsg] = useState(null);
  const [newUserData, setNewUserData] = useState({
    name: "",
    email: "",
    role: "editor",
    password: "Staff@" + Math.floor(1e3 + Math.random() * 9e3),
    status: "locked"
  });
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [error, setError] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const canManage = currentUser.role === "super_admin";
  const lockedUsersCount = users.filter((u) => u.status === "locked" || u.status === "pending").length;
  const activeUsersCount = users.filter((u) => u.status === "active").length;
  const filteredUsers = users.filter((u) => {
    if (statusFilter === "all") return true;
    if (statusFilter === "locked") return u.status === "locked" || u.status === "pending";
    return u.status === statusFilter;
  });
  const generateRandomPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    let pass = "Bmm#";
    for (let i = 0; i < 8; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUserData((prev) => ({ ...prev, password: pass }));
  };
  const generateResetPassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
    let pass = "Bmm#";
    for (let i = 0; i < 8; i++) {
      pass += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(pass);
  };
  const handleAddUser = (e) => {
    e.preventDefault();
    setError(null);
    if (!newUserData.name.trim() || !newUserData.email.trim()) {
      setError("Please provide full name and email address.");
      return;
    }
    if (!newUserData.password || newUserData.password.length < 4) {
      setError("Password must be at least 4 characters long.");
      return;
    }
    const res = StorageService.registerUser({
      name: newUserData.name.trim(),
      email: newUserData.email.trim(),
      password: newUserData.password,
      role: newUserData.role
    });
    if (res.success && res.user) {
      setIsAdding(false);
      setNewUserData({
        name: "",
        email: "",
        role: "editor",
        password: "Staff@" + Math.floor(1e3 + Math.random() * 9e3),
        status: "locked"
      });
      setError(null);
      setActionSuccessMsg(
        `User "${res.user.name}" created and will REMAIN LOCKED until Super Admin clicks "Approve & Unlock".`
      );
      setTimeout(() => setActionSuccessMsg(null), 5e3);
      onUsersUpdated();
    } else {
      setError(res.error || "Failed to add user.");
    }
  };
  const handleApproveAndUnlock = (user) => {
    StorageService.approveUser(user.id);
    setActionSuccessMsg(`Account for "${user.name}" has been APPROVED and activated! They can now sign in.`);
    setTimeout(() => setActionSuccessMsg(null), 3500);
    onUsersUpdated();
  };
  const handleLockAccount = (user) => {
    if (user.id === currentUser.id) {
      alert("You cannot lock your own active administrator account.");
      return;
    }
    StorageService.lockUser(user.id);
    setActionSuccessMsg(`Account for "${user.name}" has been LOCKED.`);
    setTimeout(() => setActionSuccessMsg(null), 3500);
    onUsersUpdated();
  };
  const handleSaveResetPassword = (e) => {
    e.preventDefault();
    if (!resettingUser || !newPassword) return;
    StorageService.resetUserPassword(resettingUser.id, newPassword);
    setActionSuccessMsg(`Password for "${resettingUser.name}" successfully updated with Bcrypt security!`);
    setTimeout(() => setActionSuccessMsg(null), 3500);
    setResettingUser(null);
    setNewPassword("");
    onUsersUpdated();
  };
  const handleToggleStatus = (user) => {
    if (user.id === currentUser.id) {
      alert("You cannot modify your own status while logged in.");
      return;
    }
    const newStatus = user.status === "active" ? "suspended" : "active";
    StorageService.saveUser({
      ...user,
      status: newStatus
    });
    setActionSuccessMsg(`Status for "${user.name}" changed to ${newStatus}.`);
    setTimeout(() => setActionSuccessMsg(null), 3e3);
    onUsersUpdated();
  };
  const handleRoleChange = (targetUser, newRole) => {
    if (targetUser.id === currentUser.id) {
      alert("You cannot change your own role while logged in.");
      return;
    }
    if (targetUser.role === newRole) return;
    StorageService.saveUser({
      ...targetUser,
      role: newRole
    });
    setActionSuccessMsg(`Role for "${targetUser.name}" changed to "${newRole.replace("_", " ")}". Saved to MySQL.`);
    setTimeout(() => setActionSuccessMsg(null), 3500);
    onUsersUpdated();
  };
  const handleDeleteUser = (userId) => {
    if (userId === currentUser.id) {
      alert("You cannot delete your own account.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this staff user account?")) {
      StorageService.deleteUser(userId);
      setActionSuccessMsg("User account deleted from MySQL database.");
      setTimeout(() => setActionSuccessMsg(null), 3e3);
      onUsersUpdated();
    }
  };
  return <div className="space-y-6">
      {
    /* Top Header */
  }
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-serif font-bold text-slate-900 tracking-tight">
            User & Staff Management (Bcrypt Secured)
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Manage administrative staff, permissions, Bcrypt password hashing, and user approval moderation.
          </p>
        </div>

        {canManage && <button
    onClick={() => {
      setIsAdding(true);
      setError(null);
    }}
    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-800 text-white rounded-xl text-xs font-semibold hover:bg-emerald-900 shadow-sm cursor-pointer transition-colors"
  >
            <UserPlus className="w-4 h-4" />
            <span>Create New User</span>
          </button>}
      </div>

      {
    /* Action Notification */
  }
      {actionSuccessMsg && <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{actionSuccessMsg}</span>
        </div>}

      {
    /* Pending Approval Alert Banner for Super Admin */
  }
      {lockedUsersCount > 0 && canManage && <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl text-amber-950 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center font-bold shrink-0">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <strong className="font-bold text-amber-900 block">
                {lockedUsersCount} Account{lockedUsersCount > 1 ? "s" : ""} Locked Pending Approval
              </strong>
              <span className="text-[11px] text-amber-800">
                Newly created users remain locked and cannot sign in until you review and approve them.
              </span>
            </div>
          </div>
          <button
    onClick={() => setStatusFilter("locked")}
    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer self-start sm:self-auto shrink-0"
  >
            Review Pending Accounts ({lockedUsersCount})
          </button>
        </div>}

      {!canManage && <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs flex items-center gap-2">
          <Info className="w-4 h-4 text-amber-600 shrink-0" />
          <span>Notice: Only <strong>Super Admin</strong> can approve locked accounts, reset security passwords, or delete staff accounts.</span>
        </div>}

      {
    /* Filter Tabs */
  }
      <div className="flex flex-wrap items-center gap-2 border-b border-stone-200 pb-3">
        <button
    onClick={() => setStatusFilter("all")}
    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${statusFilter === "all" ? "bg-emerald-950 text-white shadow-xs" : "bg-white text-slate-700 border border-stone-200 hover:bg-stone-50"}`}
  >
          All Accounts ({users.length})
        </button>
        <button
    onClick={() => setStatusFilter("locked")}
    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer flex items-center gap-1.5 ${statusFilter === "locked" ? "bg-amber-600 text-white shadow-xs" : "bg-amber-50 text-amber-900 border border-amber-300 hover:bg-amber-100"}`}
  >
          <Lock className="w-3.5 h-3.5" />
          <span>Pending Approval ({lockedUsersCount})</span>
        </button>
        <button
    onClick={() => setStatusFilter("active")}
    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${statusFilter === "active" ? "bg-emerald-800 text-white shadow-xs" : "bg-white text-slate-700 border border-stone-200 hover:bg-stone-50"}`}
  >
          Active ({activeUsersCount})
        </button>
      </div>

      {
    /* Add User Modal */
  }
      {isAdding && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6 border border-stone-200 space-y-5 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-stone-200 pb-3">
              <div>
                <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-emerald-800" />
                  <span>Create New User</span>
                </h2>
                <p className="text-[11px] text-slate-500">
                  Password will be secured with standard Bcrypt hashing and created in Locked status
                </p>
              </div>
              <button
    onClick={() => setIsAdding(false)}
    className="p-1 rounded-lg text-slate-400 hover:text-slate-700 cursor-pointer"
  >
                <X className="w-5 h-5" />
              </button>
            </div>

            {error && <div className="p-2.5 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>}

            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Full Name & Honorific *
                </label>
                <input
    type="text"
    required
    value={newUserData.name}
    onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
    placeholder="e.g. Maulana Mahmudul Hasan"
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Staff Email Address (Login Username) *
                </label>
                <input
    type="email"
    required
    value={newUserData.email}
    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
    placeholder="mahmud@baitulmuqaddas.edu.bd"
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    System Role *
                  </label>
                  <select
    value={newUserData.role}
    onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
    className="w-full px-3 py-2 text-xs rounded-lg border border-stone-300 bg-white"
  >
                    <option value="editor">Editor (Can create & edit content)</option>
                    <option value="teacher">Teacher / Ustadh (Curriculum & academic updates)</option>
                    <option value="principal">Principal / Muhtamim (Academic & administrative leader)</option>
                    <option value="moderator">Moderator (Can review admissions & inquiries)</option>
                    <option value="accountant">Accountant (Accounts & administrative records)</option>
                    <option value="super_admin">Super Admin (Full administrative access)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Account Status
                  </label>
                  <div className="w-full px-3 py-2 text-xs rounded-lg border border-amber-300 bg-amber-50 text-amber-900 font-semibold flex items-center gap-1.5">
                    <Lock className="w-3.5 h-3.5 text-amber-700" />
                    <span>Locked (Pending Approval)</span>
                  </div>
                  <span className="text-[10px] text-slate-500 mt-1 block">
                    Security Policy: All newly created users remain locked until approved.
                  </span>
                </div>
              </div>

              {
    /* Password & Security Section */
  }
              <div className="p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                    <KeyRound className="w-3.5 h-3.5 text-amber-600" />
                    <span>Password Security Setup *</span>
                  </label>
                  <button
    type="button"
    onClick={generateRandomPassword}
    className="text-[11px] font-semibold text-emerald-800 hover:text-emerald-950 flex items-center gap-1 cursor-pointer"
  >
                    <Sparkles className="w-3 h-3" />
                    <span>Generate Strong Password</span>
                  </button>
                </div>

                <div className="relative">
                  <input
    type={showNewUserPassword ? "text" : "password"}
    required
    value={newUserData.password}
    onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
    placeholder="Enter password..."
    className="w-full pl-3 pr-10 py-2 text-xs rounded-lg border border-stone-300 bg-white font-mono focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
                  <button
    type="button"
    onClick={() => setShowNewUserPassword(!showNewUserPassword)}
    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
    title={showNewUserPassword ? "Hide password" : "Show password"}
  >
                    {showNewUserPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
                  <span>Password will be hashed using Bcrypt before storage in MySQL `users` table.</span>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-stone-200">
                <button
    type="button"
    onClick={() => setIsAdding(false)}
    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-stone-100 rounded-lg cursor-pointer"
  >
                  Cancel
                </button>
                <button
    type="submit"
    className="px-4 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm cursor-pointer"
  >
                  Create User (Locked)
                </button>
              </div>
            </form>
          </div>
        </div>}

      {
    /* Reset Password Modal */
  }
      {resettingUser && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-6 border border-stone-200 space-y-4">
            <div className="flex items-center justify-between border-b border-stone-200 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-1.5">
                  <KeyRound className="w-4 h-4 text-amber-600" />
                  <span>Reset Staff Password</span>
                </h3>
                <p className="text-[11px] text-slate-500">
                  User: <strong className="text-slate-700">{resettingUser.name}</strong> ({resettingUser.email})
                </p>
              </div>
              <button
    onClick={() => setResettingUser(null)}
    className="p-1 rounded-lg text-slate-400 hover:text-slate-700 cursor-pointer"
  >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveResetPassword} className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-slate-700">
                    New Password *
                  </label>
                  <button
    type="button"
    onClick={generateResetPassword}
    className="text-[11px] font-semibold text-emerald-800 hover:underline cursor-pointer"
  >
                    Generate Random
                  </button>
                </div>
                <div className="relative">
                  <input
    type={showResetPassword ? "text" : "password"}
    required
    value={newPassword}
    onChange={(e) => setNewPassword(e.target.value)}
    placeholder="Enter new password..."
    className="w-full pl-3 pr-10 py-2 text-xs rounded-lg border border-stone-300 font-mono focus:outline-hidden focus:ring-1 focus:ring-emerald-700"
  />
                  <button
    type="button"
    onClick={() => setShowResetPassword(!showResetPassword)}
    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
    title={showResetPassword ? "Hide password" : "Show password"}
  >
                    {showResetPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="text-[11px] text-slate-500 flex items-center gap-1.5 bg-stone-50 p-2.5 rounded-lg border border-stone-200">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
                <span>New password will be securely re-hashed using Bcrypt.</span>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-stone-200">
                <button
    type="button"
    onClick={() => setResettingUser(null)}
    className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-stone-100 rounded-lg cursor-pointer"
  >
                  Cancel
                </button>
                <button
    type="submit"
    className="px-4 py-2 text-xs font-bold text-white bg-emerald-800 hover:bg-emerald-900 rounded-lg shadow-sm cursor-pointer"
  >
                  Save Bcrypt Password
                </button>
              </div>
            </form>
          </div>
        </div>}

      {
    /* Users Table */
  }
      <div className="bg-white rounded-2xl border border-stone-200 shadow-xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-stone-50 border-b border-stone-200 text-slate-600">
              <tr>
                <th className="py-3 px-4 font-semibold">User Profile</th>
                <th className="py-3 px-4 font-semibold">Role</th>
                <th className="py-3 px-4 font-semibold">Security Hash</th>
                <th className="py-3 px-4 font-semibold">Status</th>
                <th className="py-3 px-4 font-semibold">Last Login</th>
                <th className="py-3 px-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {filteredUsers.map((u) => {
    const isLocked = u.status === "locked" || u.status === "pending";
    return <tr key={u.id} className="hover:bg-stone-50/60 transition-colors">
                    <td className="py-3 px-4 font-semibold text-slate-900">
                      <div className="flex items-center gap-3">
                        <img
      src={u.avatar || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"}
      alt=""
      className="w-8 h-8 rounded-full object-cover border border-stone-200"
    />
                        <div>
                          <div>{u.name}</div>
                          <div className="text-[11px] text-slate-500 font-normal">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {canManage && u.id !== currentUser.id ? <select
      value={u.role}
      onChange={(e) => handleRoleChange(u, e.target.value)}
      className={`text-[11px] font-bold rounded-lg px-2.5 py-1 border cursor-pointer focus:outline-hidden focus:ring-1 focus:ring-emerald-700 transition-colors ${u.role === "super_admin" ? "bg-purple-50 text-purple-900 border-purple-300 hover:bg-purple-100" : u.role === "principal" ? "bg-emerald-50 text-emerald-900 border-emerald-300 hover:bg-emerald-100" : u.role === "teacher" ? "bg-indigo-50 text-indigo-900 border-indigo-300 hover:bg-indigo-100" : u.role === "editor" ? "bg-blue-50 text-blue-900 border-blue-300 hover:bg-blue-100" : u.role === "moderator" ? "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100" : "bg-teal-50 text-teal-900 border-teal-300 hover:bg-teal-100"}`}
      title="Click to change staff role"
    >
                          <option value="editor">Editor</option>
                          <option value="teacher">Teacher / Ustadh</option>
                          <option value="principal">Principal / Muhtamim</option>
                          <option value="moderator">Moderator</option>
                          <option value="accountant">Accountant</option>
                          <option value="super_admin">Super Admin</option>
                        </select> : <span className={`inline-block px-2 py-0.5 text-[10px] font-bold rounded uppercase ${u.role === "super_admin" ? "bg-purple-100 text-purple-800" : u.role === "principal" ? "bg-emerald-100 text-emerald-800" : u.role === "teacher" ? "bg-indigo-100 text-indigo-800" : u.role === "editor" ? "bg-blue-100 text-blue-800" : u.role === "moderator" ? "bg-amber-100 text-amber-800" : "bg-teal-100 text-teal-800"}`}>
                          {(u.role || "").replace("_", " ")}
                        </span>}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                        <ShieldCheck className="w-3 h-3 text-emerald-600" />
                        <span>Bcrypt</span>
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {isLocked ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded uppercase bg-amber-100 text-amber-900 border border-amber-300">
                          <Lock className="w-3 h-3 text-amber-700" />
                          <span>Locked (Pending Approval)</span>
                        </span> : u.status === "active" ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded uppercase bg-emerald-100 text-emerald-800">
                          <Check className="w-3 h-3 text-emerald-700" />
                          <span>Active</span>
                        </span> : <span className="inline-block px-2 py-0.5 text-[10px] font-bold rounded uppercase bg-red-100 text-red-800">
                          {u.status}
                        </span>}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-500 tabular-nums">
                      {u.last_login || "Never"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {canManage && <div className="inline-flex items-center gap-1.5">
                          {
      /* Super Admin Approve & Unlock Button */
    }
                          {isLocked && <button
      onClick={() => handleApproveAndUnlock(u)}
      className="px-2.5 py-1 text-[11px] font-bold rounded bg-emerald-700 hover:bg-emerald-800 text-white shadow-xs cursor-pointer flex items-center gap-1"
      title="Approve and activate this user account"
    >
                              <ShieldCheck className="w-3.5 h-3.5 text-amber-300" />
                              <span>Approve & Unlock</span>
                            </button>}

                          {
      /* Lock Account Button for Active Users */
    }
                          {!isLocked && u.id !== currentUser.id && u.status === "active" && <button
      onClick={() => handleLockAccount(u)}
      className="px-2 py-1 text-[11px] font-medium rounded border border-amber-300 text-amber-900 bg-amber-50 hover:bg-amber-100 cursor-pointer flex items-center gap-1"
      title="Lock account so user cannot sign in until unlocked"
    >
                              <Lock className="w-3 h-3 text-amber-700" />
                              <span>Lock</span>
                            </button>}

                          <button
      onClick={() => {
        setResettingUser(u);
        setNewPassword("");
      }}
      className="px-2 py-1 text-[11px] font-medium rounded border border-stone-200 text-slate-700 hover:bg-stone-100 cursor-pointer flex items-center gap-1"
      title="Reset password"
    >
                            <KeyRound className="w-3 h-3 text-amber-600" />
                            <span>Password</span>
                          </button>

                          {u.id !== currentUser.id && <>
                              <button
      onClick={() => handleToggleStatus(u)}
      className="px-2 py-1 text-[11px] font-medium rounded border border-stone-200 text-slate-700 hover:bg-stone-100 cursor-pointer"
    >
                                {u.status === "active" ? "Suspend" : "Activate"}
                              </button>
                              <button
      onClick={() => handleDeleteUser(u.id)}
      className="text-red-600 hover:text-red-800 text-[11px] font-semibold cursor-pointer px-1.5 py-1"
    >
                                Delete
                              </button>
                            </>}
                        </div>}
                    </td>
                  </tr>;
  })}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};
